import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Box, Checkbox, Typography, CircularProgress, TextField, Switch, Modal } from "@mui/material";
import { TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { RootState, useAppDispatch } from "../../store";
import { Agent, AgentType, EHR, Organization } from "../../types";
import { updateAgentThunk } from "../../slices/AgentSlice";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { InputStyles } from "../../styles/GeneralStyles";
import { Colors } from "../../Colors";
import { PrimaryButton, TransparentButton } from "../../subcomponents/CustomButton";
import { updateOrganizationAttribute, setInboundRedirectNumber } from "../../slices/AuthSlice";
import { convertE164ToReadable, convertToCallablePhoneNumber, estimateSmsSegments } from "../../utils/utils";
import { StyledTextField } from "../../components/text-fields/StyledTextField";
import { NumberTextField } from "../../components/text-fields/NumberTextField";
import SettingsLayout from "./SettingsLayout";
import SettingRow from "./SettingRow";
import { HelpTooltip } from "../../subcomponents/HelpTooltip";

const templateVariableDescriptions = {
  patientFirstName: "The patient's first name",
  patientLastName: "The patient's last name",
  patientPreferredName: "The patient's preferred name (defaults to first name if preferred name is not set)",
  organizationName: "The organization's name",
  agentName: "The agent's name",
  providerName: "The provider's name",
  appointmentDate: "The appointment date",
  appointmentTime: "The appointment time",
  appointmentTypeName: "The appointment type",
  clinicPhoneNumber: "The clinic's phone number",
  link: "The relevant link",
  pcpName: "[DEPRECATED] The provider's name (use providerName instead)",
  daysBeforeReminder: "The number of days before the appointment for the reminder",
};

const fakeTemplateValues: Record<keyof typeof templateVariableDescriptions, string> = {
  patientFirstName: "Jane",
  patientLastName: "Smith",
  patientPreferredName: "Jane",
  organizationName: "Metro PT",
  agentName: "Penny",
  providerName: "Dr. Johnson",
  appointmentDate: "Monday, May 20th at 2:00 PM",
  appointmentTime: "2:00 PM",
  appointmentTypeName: "Follow-Up",
  clinicPhoneNumber: "(555) 123-4567",
  link: "https://penciled.com/patient/abc123",
  pcpName: "Dr. Johnson",
  daysBeforeReminder: "1",
};

type MessageTemplateRowProps = {
  label: string;
  value: string | undefined;
  description: string;
  templateVariables: (string & keyof typeof templateVariableDescriptions)[];
  example: string;
  onChange: (value: string) => void;
  valueOverrides?: Partial<Record<keyof typeof templateVariableDescriptions, string>>;
};

function MessageTemplateRow({ label, value, description, templateVariables, example, onChange, valueOverrides }: MessageTemplateRowProps) {
  const [showPreview, setShowPreview] = useState(true);
  const segments = estimateSmsSegments(value || "");

  const resolvedValues = useMemo(() => ({ ...fakeTemplateValues, ...valueOverrides }), [valueOverrides]);

  const previewContent = useMemo(() => {
    const source = value || example;
    const parts: React.ReactNode[] = [];
    const regex = /\{\{\s*(\w+)\s*\}\}/g;
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = regex.exec(source)) !== null) {
      if (match.index > lastIndex) {
        parts.push(<span key={key++}>{source.slice(lastIndex, match.index)}</span>);
      }
      const varName = match[1] as keyof typeof templateVariableDescriptions;
      const isValid = varName in templateVariableDescriptions && templateVariables.includes(varName);
      parts.push(
        isValid ? (
          <span key={key++} style={{ color: Colors.primary }}>
            {resolvedValues[varName]}
          </span>
        ) : (
          <span key={key++} style={{ color: Colors.error }}>
            {match[0]}
          </span>
        ),
      );
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < source.length) {
      parts.push(<span key={key++}>{source.slice(lastIndex)}</span>);
    }

    return parts;
  }, [value, example, templateVariables, resolvedValues]);

  return (
    <SettingRow
      label={label}
      stacked
      help={
        <HelpTooltip
          title={
            <div>
              {description}
              <br />
              <br />
              You can use the following variables in the text:
              {templateVariables.map((variable) => (
                <>
                  <br />- &#123;&#123; {variable} &#125;&#125;: {templateVariableDescriptions[variable]}
                </>
              ))}
              <br />
              <br />
              Make sure to include the brackets.
              <br />
              Example:
              <br />
              {example}
            </div>
          }
        />
      }
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <TextField
            multiline={true}
            placeholder={example}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            variant="outlined"
            sx={{
              width: "100%",
              "& .MuiInputBase-input": {
                color: "#FFF",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                background: Colors.textfield,
              },
              "& .MuiOutlinedInput-input": {
                padding: "6px 15px",
              },
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mt: "4px" }}>
            <Typography variant="caption" color={segments.segments > 10 ? Colors.error : Colors.grey2}>
              {segments.segments.toFixed(2)} segments ({segments.encoding}) <br />
              {segments.forbiddenChars.length > 0 ? `Special chars: ${segments.forbiddenChars.join("")}` : ""}
            </Typography>
            <Typography variant="caption" onClick={() => setShowPreview((v) => !v)} sx={{ cursor: "pointer", color: Colors.primary, textDecoration: "underline", whiteSpace: "nowrap", ml: 1 }}>
              {showPreview ? "Hide preview" : "Preview"}
            </Typography>
          </Box>
        </Box>
        {showPreview && (
          <Box
            sx={{
              flex: 1,
              padding: "8px 15px",
              background: Colors.textfield,
              borderRadius: "10px",
              fontSize: "0.875rem",
              color: "#FFF",
              whiteSpace: "pre-wrap",
              lineHeight: 1.6,
            }}
          >
            {previewContent}
          </Box>
        )}
      </Box>
    </SettingRow>
  );
}

export default function AgentSettings() {
  const dispatch = useAppDispatch();
  const { showMessage } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector((state: RootState) => state.auth.organization);
  const agents = useSelector((state: RootState) => state.agents.agents);
  const agent = useMemo(() => agents && agents.find((agent) => agent.agentType === AgentType.waitlist), [agents]);

  const [isActive, setIsActive] = useState<boolean>(agent?.isActive || false);
  const [agentName, setAgentName] = useState<string | undefined>(agent?.name);
  const [callbackNumber, setCallbackNumber] = useState<string | undefined>(organization?.phoneNumber);
  const [inboundRedirectNumber, setInboundRedirectNumberState] = useState<string | undefined>(organization?.inboundRedirectNumber);
  const [emergencyTransferNumber, setEmergencyTransferNumberState] = useState<string | undefined>(organization?.emergencyTransferNumber);
  const [workingHoursStart, setWorkingHoursStart] = useState<DateTime | null>(null);
  const [workingHoursEnd, setWorkingHoursEnd] = useState<DateTime | null>(null);

  const [address, setAddress] = useState<string | undefined>(agent?.address);
  const [workingHours, setWorkingHours] = useState<string | undefined>(agent?.workingHours);

  const [welcomeText, setWelcomeText] = useState<string | undefined>(agent?.welcomeText);
  const [appointmentAvailableText, setAppointmentAvailableText] = useState<string | undefined>(agent?.appointmentAvailableText);
  const [appointmentBookedText, setAppointmentBookedText] = useState<string | undefined>(agent?.appointmentBookedText);
  const [appointmentTakenText, setAppointmentTakenText] = useState<string | undefined>(agent?.appointmentTakenText);
  const [initialExamText, setInitialExamText] = useState<string | undefined>(agent?.initialExamText);
  const [planOfCareCreatedText, setPlanOfCareCreatedText] = useState<string | undefined>(agent?.planOfCareCreatedText);
  const [planOfCareUpdatedText, setPlanOfCareUpdatedText] = useState<string | undefined>(agent?.planOfCareUpdatedText);
  const [planOfCareAutomatedText, setPlanOfCareAutomatedText] = useState<string | undefined>(agent?.planOfCareAutomatedText);
  const [dischargedText, setDischargedText] = useState<string | undefined>(agent?.dischargedText);
  const [autoDischargedText, setAutoDischargedText] = useState<string | undefined>(agent?.autoDischargedText);
  const [reminderText, setReminderText] = useState<string | undefined>(agent?.reminderText);
  const [appointmentCancelledText, setAppointmentCancelledText] = useState<string | undefined>(agent?.appointmentCancelledText);
  const [checkedInPocLinkText, setCheckedInPocLinkText] = useState<string | undefined>(agent?.checkedInPocLinkText);

  const [welcomeTextEs, setWelcomeTextEs] = useState<string | undefined>(agent?.welcomeTextEs);
  const [appointmentAvailableTextEs, setAppointmentAvailableTextEs] = useState<string | undefined>(agent?.appointmentAvailableTextEs);
  const [appointmentBookedTextEs, setAppointmentBookedTextEs] = useState<string | undefined>(agent?.appointmentBookedTextEs);
  const [appointmentTakenTextEs, setAppointmentTakenTextEs] = useState<string | undefined>(agent?.appointmentTakenTextEs);
  const [initialExamTextEs, setInitialExamTextEs] = useState<string | undefined>(agent?.initialExamTextEs);
  const [planOfCareCreatedTextEs, setPlanOfCareCreatedTextEs] = useState<string | undefined>(agent?.planOfCareCreatedTextEs);
  const [planOfCareUpdatedTextEs, setPlanOfCareUpdatedTextEs] = useState<string | undefined>(agent?.planOfCareUpdatedTextEs);
  const [planOfCareAutomatedTextEs, setPlanOfCareAutomatedTextEs] = useState<string | undefined>(agent?.planOfCareAutomatedTextEs);
  const [dischargedTextEs, setDischargedTextEs] = useState<string | undefined>(agent?.dischargedTextEs);
  const [autoDischargedTextEs, setAutoDischargedTextEs] = useState<string | undefined>(agent?.autoDischargedTextEs);
  const [reminderTextEs, setReminderTextEs] = useState<string | undefined>(agent?.reminderTextEs);
  const [appointmentCancelledTextEs, setAppointmentCancelledTextEs] = useState<string | undefined>(agent?.appointmentCancelledTextEs);
  const [checkedInPocLinkTextEs, setCheckedInPocLinkTextEs] = useState<string | undefined>(agent?.checkedInPocLinkTextEs);

  const [showSpanishTemplates, setShowSpanishTemplates] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [broadcastMessage, setBroadcastMessage] = useState<string>("");
  const [isBroadcastConfirmOpen, setIsBroadcastConfirmOpen] = useState<boolean>(false);
  const [isBroadcasting, setIsBroadcasting] = useState<boolean>(false);

  useEffect(() => {
    if (agent) {
      setIsActive(agent.isActive);
      setAgentName(agent.name);
      setAddress(agent.address);
      setWorkingHours(agent.workingHours);
      setWelcomeText(agent.welcomeText);
      setAppointmentAvailableText(agent.appointmentAvailableText);
      setAppointmentBookedText(agent.appointmentBookedText);
      setAppointmentTakenText(agent.appointmentTakenText);
      setInitialExamText(agent.initialExamText);
      setPlanOfCareCreatedText(agent.planOfCareCreatedText);
      setPlanOfCareUpdatedText(agent.planOfCareUpdatedText);
      setPlanOfCareAutomatedText(agent.planOfCareAutomatedText);
      setDischargedText(agent.dischargedText);
      setAutoDischargedText(agent.autoDischargedText);
      setReminderText(agent.reminderText);
      setAppointmentCancelledText(agent.appointmentCancelledText);
      setCheckedInPocLinkText(agent.checkedInPocLinkText);
      setWelcomeTextEs(agent.welcomeTextEs);
      setAppointmentAvailableTextEs(agent.appointmentAvailableTextEs);
      setAppointmentBookedTextEs(agent.appointmentBookedTextEs);
      setAppointmentTakenTextEs(agent.appointmentTakenTextEs);
      setInitialExamTextEs(agent.initialExamTextEs);
      setPlanOfCareCreatedTextEs(agent.planOfCareCreatedTextEs);
      setPlanOfCareUpdatedTextEs(agent.planOfCareUpdatedTextEs);
      setPlanOfCareAutomatedTextEs(agent.planOfCareAutomatedTextEs);
      setDischargedTextEs(agent.dischargedTextEs);
      setAutoDischargedTextEs(agent.autoDischargedTextEs);
      setReminderTextEs(agent.reminderTextEs);
      setAppointmentCancelledTextEs(agent.appointmentCancelledTextEs);
      setCheckedInPocLinkTextEs(agent.checkedInPocLinkTextEs);
    } else {
      setIsActive(false);
      setAgentName(undefined);
      setAddress(undefined);
      setWorkingHours(undefined);
      setWelcomeText(undefined);
      setAppointmentAvailableText(undefined);
      setAppointmentBookedText(undefined);
      setAppointmentTakenText(undefined);
      setInitialExamText(undefined);
      setPlanOfCareCreatedText(undefined);
      setPlanOfCareUpdatedText(undefined);
      setPlanOfCareAutomatedText(undefined);
      setDischargedText(undefined);
      setAutoDischargedText(undefined);
      setReminderText(undefined);
      setAppointmentCancelledText(undefined);
      setCheckedInPocLinkText(undefined);
      setWelcomeTextEs(undefined);
      setAppointmentAvailableTextEs(undefined);
      setAppointmentBookedTextEs(undefined);
      setAppointmentTakenTextEs(undefined);
      setInitialExamTextEs(undefined);
      setPlanOfCareCreatedTextEs(undefined);
      setPlanOfCareUpdatedTextEs(undefined);
      setPlanOfCareAutomatedTextEs(undefined);
      setDischargedTextEs(undefined);
      setAutoDischargedTextEs(undefined);
      setReminderTextEs(undefined);
      setAppointmentCancelledTextEs(undefined);
      setCheckedInPocLinkTextEs(undefined);
    }
  }, [agent]);

  useEffect(() => {
    if (organization) {
      setCallbackNumber(organization.phoneNumber);
      setInboundRedirectNumberState(organization.inboundRedirectNumber);
      setEmergencyTransferNumberState(organization.emergencyTransferNumber);
      setWorkingHoursStart(organization.workingHoursStart ? DateTime.fromISO(organization.workingHoursStart) : null);
      setWorkingHoursEnd(organization.workingHoursEnd ? DateTime.fromISO(organization.workingHoursEnd) : null);
    }
  }, [organization]);

  const handleSave = async () => {
    if (!agent || !user?.token) {
      showMessage("Agent data or user session not found.", "error");
      return;
    }

    setIsLoading(true);
    try {
      const agentProps: Partial<Agent> = {
        isActive: isActive,
        agentId: agent.agentId,
        name: agentName,
        address: address,
        workingHours: workingHours,
        welcomeText: welcomeText,
        appointmentAvailableText: appointmentAvailableText,
        appointmentBookedText: appointmentBookedText,
        appointmentTakenText: appointmentTakenText,
        initialExamText: initialExamText,
        planOfCareCreatedText: planOfCareCreatedText,
        planOfCareUpdatedText: planOfCareUpdatedText,
        planOfCareAutomatedText: planOfCareAutomatedText,
        dischargedText: dischargedText,
        autoDischargedText: autoDischargedText,
        reminderText: reminderText,
        appointmentCancelledText: appointmentCancelledText,
        checkedInPocLinkText: checkedInPocLinkText,
        welcomeTextEs: welcomeTextEs,
        appointmentAvailableTextEs: appointmentAvailableTextEs,
        appointmentBookedTextEs: appointmentBookedTextEs,
        appointmentTakenTextEs: appointmentTakenTextEs,
        initialExamTextEs: initialExamTextEs,
        planOfCareCreatedTextEs: planOfCareCreatedTextEs,
        planOfCareUpdatedTextEs: planOfCareUpdatedTextEs,
        planOfCareAutomatedTextEs: planOfCareAutomatedTextEs,
        dischargedTextEs: dischargedTextEs,
        autoDischargedTextEs: autoDischargedTextEs,
        reminderTextEs: reminderTextEs,
        appointmentCancelledTextEs: appointmentCancelledTextEs,
        checkedInPocLinkTextEs: checkedInPocLinkTextEs,
      };

      const promises = [];

      promises.push(dispatch(updateAgentThunk({ token: user.token, agent: agentProps })).unwrap());

      if (callbackNumber) {
        promises.push(
          dispatch(
            updateOrganizationAttribute({
              token: user.token,
              attribute: "phoneNumber",
              value: convertToCallablePhoneNumber(callbackNumber),
            }),
          ).unwrap(),
        );
      }

      if (emergencyTransferNumber && emergencyTransferNumber.trim() !== "") {
        promises.push(
          dispatch(
            updateOrganizationAttribute({
              token: user.token,
              attribute: "emergencyTransferNumber",
              value: convertToCallablePhoneNumber(emergencyTransferNumber),
            }),
          ).unwrap(),
        );
      }

      if (inboundRedirectNumber && inboundRedirectNumber.trim() !== "") {
        promises.push(
          dispatch(
            setInboundRedirectNumber({
              token: user.token,
              inboundRedirectNumber: convertToCallablePhoneNumber(inboundRedirectNumber),
            }),
          ).unwrap(),
        );
      }

      if (workingHoursStart) {
        promises.push(
          dispatch(
            updateOrganizationAttribute({
              token: user.token,
              attribute: "workingHoursStart",
              value: workingHoursStart.toISO(),
            }),
          ).unwrap(),
        );
      }

      if (workingHoursEnd) {
        promises.push(
          dispatch(
            updateOrganizationAttribute({
              token: user.token,
              attribute: "workingHoursEnd",
              value: workingHoursEnd.toISO(),
            }),
          ).unwrap(),
        );
      }

      await Promise.all(promises);
      showMessage("Agent settings updated successfully.", "success");
    } catch (error) {
      console.error("Failed to update agent settings:", error);
      showMessage("Failed to update agent settings.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBroadcast = async () => {
    if (!user?.token || !broadcastMessage.trim()) {
      return;
    }

    setIsBroadcasting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/texts/broadcast`,
        { message: broadcastMessage },
        { headers: { Authorization: `Bearer ${user.token}` } },
      );

      setBroadcastMessage("");
      showMessage("Broadcast text sent to all patients.", "success");
    } catch (error: any) {
      console.error("Failed to send broadcast text:", error);
      showMessage(error.message || "Failed to send broadcast text.", "error");
    } finally {
      setIsBroadcasting(false);
      setIsBroadcastConfirmOpen(false);
    }
  };

  const templateValueOverrides: Partial<Record<keyof typeof templateVariableDescriptions, string>> = {
    ...(agentName && { agentName }),
    ...(organization?.name && { organizationName: organization.name }),
    ...(callbackNumber && { clinicPhoneNumber: convertE164ToReadable(callbackNumber) }),
    ...(agent?.reminderDaysBeforeAppointment && { daysBeforeReminder: agent.reminderDaysBeforeAppointment.toString() }),
  };

  if (!agent) {
    return (
      <SettingsLayout>
        <CircularProgress size={30} />
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout>
      <Box pb={6}>
        <Typography variant="h5">
          Agent{" "}
          <Switch
            checked={isActive}
            onChange={() => setIsActive(!isActive)}
            sx={{
              "& .MuiSwitch-switchBase": {
                color: "#FFFFFF",
                "& + .MuiSwitch-track": {
                  backgroundColor: "#CCCCCC",
                },
              },
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#FFFFFF",
                "& + .MuiSwitch-track": {
                  backgroundColor: Colors.primary,
                },
              },
            }}
          />
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
            Agent Information
          </Typography>
          <SettingRow label="Agent ID">
            <Typography variant="body1">{agent.agentId}</Typography>
          </SettingRow>
          <SettingRow label="Agent name">
            <StyledTextField value={agentName} onChange={(value) => setAgentName(value)} />
          </SettingRow>
          <SettingRow label="Callback number">
            <StyledTextField value={callbackNumber} onChange={(value) => setCallbackNumber(value)} />
          </SettingRow>
          <SettingRow label="Inbound redirect number">
            <StyledTextField value={inboundRedirectNumber} onChange={(value) => setInboundRedirectNumberState(value)} />
          </SettingRow>
          <SettingRow label="Emergency transfer number">
            <StyledTextField value={emergencyTransferNumber} onChange={(value) => setEmergencyTransferNumberState(value)} />
          </SettingRow>
          <SettingRow label="Outbound text hours" description="Agent will still respond to incoming texts during off hours">
            <Box display="flex" flexDirection="row" gap="20px">
              <TimePicker label="Start Time" value={workingHoursStart} onChange={setWorkingHoursStart} disabled={isLoading} sx={InputStyles} />
              <TimePicker label="End Time" value={workingHoursEnd} onChange={setWorkingHoursEnd} disabled={isLoading} sx={InputStyles} />
            </Box>
          </SettingRow>
          <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
            Clinic Information
          </Typography>
          <SettingRow label="What is your clinic's address?" description="This address will be provided to patients when they ask the agent for the location.">
            <TextField
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              multiline
              rows={3}
              sx={{
                flex: 2,
                "& .MuiInputBase-root": {
                  background: Colors.textfield,
                  borderRadius: "8px",
                  color: Colors.white,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: Colors.grey1,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: Colors.primary,
                },
              }}
            />
          </SettingRow>
          <SettingRow label="What are your clinic's working hours?" description="These hours will be provided to patients when they ask the agent for the clinic's hours.">
            <TextField
              value={workingHours}
              onChange={(e) => setWorkingHours(e.target.value)}
              multiline
              rows={4}
              placeholder="Monday-Friday: 9:00 AM - 5:00 PM&#13;&#10;Saturday: 10:00 AM - 2:00 PM&#13;&#10;Sunday: Closed"
              sx={{
                flex: 2,
                "& .MuiInputBase-root": {
                  background: Colors.textfield,
                  borderRadius: "8px",
                  color: Colors.white,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: Colors.grey1,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: Colors.primary,
                },
              }}
            />
          </SettingRow>
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px", mt: 4, mb: 1 }}>
            <Typography variant="h6">Text Message Templates</Typography>
            <Box
              onClick={() => setShowSpanishTemplates((v) => !v)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                padding: "4px 10px 4px 4px",
                borderRadius: "20px",
                border: `1px solid ${showSpanishTemplates ? Colors.primary : "rgba(255, 255, 255, 0.2)"}`,
                backgroundColor: showSpanishTemplates ? `${Colors.primary}1A` : "transparent",
              }}
            >
              <Switch
                checked={showSpanishTemplates}
                onChange={() => setShowSpanishTemplates((v) => !v)}
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase": {
                    color: "#FFFFFF",
                    "& + .MuiSwitch-track": {
                      backgroundColor: "#CCCCCC",
                    },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#FFFFFF",
                    "& + .MuiSwitch-track": {
                      backgroundColor: Colors.primary,
                    },
                  },
                }}
              />
              <Typography variant="body2" sx={{ color: showSpanishTemplates ? "#fff" : Colors.info, fontWeight: 600 }}>
                Show Spanish variants
              </Typography>
            </Box>
          </Box>
          <MessageTemplateRow
            label="Welcome text"
            value={welcomeText}
            onChange={(value) => setWelcomeText(value)}
            description="This is the text that the agent will say to the patient when they get added to Penciled."
            templateVariables={["agentName", "organizationName", "patientFirstName", "patientLastName"]}
            example="Hello {{patientFirstName}}, this is {{agentName}} from {{organizationName}}👋 You may receive text updates or calls about your schedule. Message and data rates may apply. To opt out, reply STOP. For help, reply HELP."
            valueOverrides={templateValueOverrides}
          />
          {showSpanishTemplates && (
            <MessageTemplateRow
              label="Welcome text (Spanish)"
              value={welcomeTextEs}
              onChange={(value) => setWelcomeTextEs(value)}
              description="Spanish version of the welcome text, used when the patient's language is Spanish."
              templateVariables={["agentName", "organizationName", "patientFirstName", "patientLastName"]}
              example="Hola {{patientFirstName}}, soy {{agentName}} de {{organizationName}}👋 Es posible que reciba actualizaciones de texto o llamadas sobre su cita. Se pueden aplicar tarifas de mensajes y datos. Para cancelar, responda ALTO. Para ayuda, responda AYUDA."
              valueOverrides={templateValueOverrides}
            />
          )}
          <MessageTemplateRow
            label="Appointment available text"
            value={appointmentAvailableText}
            onChange={(value) => setAppointmentAvailableText(value)}
            description="This is the text that the agent will say to the patient when an appointment is available."
            templateVariables={["agentName", "organizationName", "patientFirstName", "patientLastName", "appointmentDate", "providerName", "appointmentTypeName", "pcpName"]}
            example="We have an availability for {{ appointmentTypeName }} on {{ appointmentDate }} with {{ providerName }}. Would you like to take it?"
            valueOverrides={templateValueOverrides}
          />
          {showSpanishTemplates && (
            <MessageTemplateRow
              label="Appointment available text (Spanish)"
              value={appointmentAvailableTextEs}
              onChange={(value) => setAppointmentAvailableTextEs(value)}
              description="Spanish version of the appointment available text, used when the patient's language is Spanish."
              templateVariables={["agentName", "organizationName", "patientFirstName", "patientLastName", "appointmentDate", "providerName", "appointmentTypeName", "pcpName"]}
              example="Tenemos disponibilidad para {{ appointmentTypeName }} el {{ appointmentDate }} con {{ providerName }}. ¿Le gustaría tomarla?"
              valueOverrides={templateValueOverrides}
            />
          )}
          <MessageTemplateRow
            label="Waitlist appointment booked text"
            value={appointmentBookedText}
            onChange={(value) => setAppointmentBookedText(value)}
            description="This is the text the agent will say to the patient when they have successfully booked an appointment from the waitlist."
            templateVariables={["patientFirstName", "patientLastName", "appointmentDate", "pcpName", "appointmentTypeName"]}
            example="Your appointment on {{ appointmentDate }} has been successfully booked! Thank you!"
            valueOverrides={templateValueOverrides}
          />
          {showSpanishTemplates && (
            <MessageTemplateRow
              label="Waitlist appointment booked text (Spanish)"
              value={appointmentBookedTextEs}
              onChange={(value) => setAppointmentBookedTextEs(value)}
              description="Spanish version of the waitlist appointment booked text, used when the patient's language is Spanish."
              templateVariables={["patientFirstName", "patientLastName", "appointmentDate", "pcpName", "appointmentTypeName"]}
              example="¡Su cita el {{ appointmentDate }} ha sido reservada con éxito! ¡Gracias!"
              valueOverrides={templateValueOverrides}
            />
          )}
          <MessageTemplateRow
            label="Waitlist appointment taken text"
            value={appointmentTakenText}
            onChange={(value) => setAppointmentTakenText(value)}
            description="This is the text the agent will say to the patient when an appointment from the waitlist is taken by another patient."
            templateVariables={["patientFirstName", "patientLastName", "appointmentDate", "pcpName"]}
            example="I'm sorry, but the appointment on {{ appointmentDate }} has been taken by another patient and is no longer available."
            valueOverrides={templateValueOverrides}
          />
          {showSpanishTemplates && (
            <MessageTemplateRow
              label="Waitlist appointment taken text (Spanish)"
              value={appointmentTakenTextEs}
              onChange={(value) => setAppointmentTakenTextEs(value)}
              description="Spanish version of the waitlist appointment taken text, used when the patient's language is Spanish."
              templateVariables={["patientFirstName", "patientLastName", "appointmentDate", "pcpName"]}
              example="Lo sentimos, la cita el {{ appointmentDate }} ya ha sido tomada por otro paciente y ya no está disponible."
              valueOverrides={templateValueOverrides}
            />
          )}
          <MessageTemplateRow
            label="Initial examination text"
            value={initialExamText}
            onChange={(value) => setInitialExamText(value)}
            description="This is the text the agent will say to the patient to book an initial examination."
            templateVariables={["patientFirstName", "patientLastName", "link", "clinicPhoneNumber"]}
            example="Hey {{ patientFirstName }}, ready to schedule your first visit? To schedule visit: {{ link }}."
            valueOverrides={templateValueOverrides}
          />
          {showSpanishTemplates && (
            <MessageTemplateRow
              label="Initial examination text (Spanish)"
              value={initialExamTextEs}
              onChange={(value) => setInitialExamTextEs(value)}
              description="Spanish version of the initial examination text, used when the patient's language is Spanish."
              templateVariables={["patientFirstName", "patientLastName", "link", "clinicPhoneNumber"]}
              example="Hola {{ patientFirstName }}, ¿listo para programar su primera visita? Para programar la visita: {{ link }}."
              valueOverrides={templateValueOverrides}
            />
          )}
          <MessageTemplateRow
            label="Checked-in POC link text"
            value={checkedInPocLinkText}
            onChange={(value) => setCheckedInPocLinkText(value)}
            description="This is the text the agent will say to the patient after their initial evaluation is checked in, prompting them to book plan of care visits."
            templateVariables={["patientFirstName", "patientLastName", "link", "clinicPhoneNumber"]}
            example="Hi {{ patientFirstName }}, if you would like to schedule follow-up appointments, please use this link: {{ link }}. If you need help, call us at {{ clinicPhoneNumber }}."
            valueOverrides={templateValueOverrides}
          />
          {showSpanishTemplates && (
            <MessageTemplateRow
              label="Checked-in POC link text (Spanish)"
              value={checkedInPocLinkTextEs}
              onChange={(value) => setCheckedInPocLinkTextEs(value)}
              description="Spanish version of the checked-in POC link text, used when the patient's language is Spanish."
              templateVariables={["patientFirstName", "patientLastName", "link", "clinicPhoneNumber"]}
              example="Hola {{ patientFirstName }}, si desea programar citas de seguimiento, use este enlace: {{ link }}. Si necesita ayuda, llámenos al {{ clinicPhoneNumber }}."
              valueOverrides={templateValueOverrides}
            />
          )}
          <MessageTemplateRow
            label="Plan of care created text"
            value={planOfCareCreatedText}
            onChange={(value) => setPlanOfCareCreatedText(value)}
            description="This is the text the agent will say to the patient when a patient has been assigned a new plan of care in penciled."
            templateVariables={["patientFirstName", "patientLastName", "link", "clinicPhoneNumber"]}
            example="Hey {{ patientFirstName }}, your therapist would like you to schedule appointments. To schedule visit: {{ link }}. You've been automatically added to the waitlist for any visits that you do not book in your plan. To update your waitlist availability, just ask."
            valueOverrides={templateValueOverrides}
          />
          {showSpanishTemplates && (
            <MessageTemplateRow
              label="Plan of care created text (Spanish)"
              value={planOfCareCreatedTextEs}
              onChange={(value) => setPlanOfCareCreatedTextEs(value)}
              description="Spanish version of the plan of care created text, used when the patient's language is Spanish."
              templateVariables={["patientFirstName", "patientLastName", "link", "clinicPhoneNumber"]}
              example="Hola {{ patientFirstName }}, su terapeuta desea que programe citas. Para programar la visita: {{ link }}. Ha sido agregado automáticamente a la lista de espera para cualquier visita que no reserve en su plan. Para actualizar su disponibilidad en la lista de espera, solo pregunte."
              valueOverrides={templateValueOverrides}
            />
          )}
          <MessageTemplateRow
            label="Manual plan of care link sent text"
            value={planOfCareUpdatedText}
            onChange={(value) => setPlanOfCareUpdatedText(value)}
            description="This is the text the agent will say to the patient when a patient's plan of care is modified in penciled."
            templateVariables={["patientFirstName", "patientLastName", "link", "clinicPhoneNumber"]}
            example="Hey {{ patientFirstName }}, your therapist would like you to schedule more appointments. To schedule visit: {{ link }}. You've been automatically added to the waitlist for any visits that you do not book in your plan. To update your waitlist availability, just ask."
            valueOverrides={templateValueOverrides}
          />
          {showSpanishTemplates && (
            <MessageTemplateRow
              label="Manual plan of care link sent text (Spanish)"
              value={planOfCareUpdatedTextEs}
              onChange={(value) => setPlanOfCareUpdatedTextEs(value)}
              description="Spanish version of the manual plan of care link sent text, used when the patient's language is Spanish."
              templateVariables={["patientFirstName", "patientLastName", "link", "clinicPhoneNumber"]}
              example="Hola {{ patientFirstName }}, su terapeuta desea que programe más citas. Para programar la visita: {{ link }}. Ha sido agregado automáticamente a la lista de espera para cualquier visita que no reserve en su plan. Para actualizar su disponibilidad en la lista de espera, solo pregunte."
              valueOverrides={templateValueOverrides}
            />
          )}
          <MessageTemplateRow
            label="Automated plan of care link sent text"
            value={planOfCareAutomatedText}
            onChange={(value) => setPlanOfCareAutomatedText(value)}
            description="This is the text the agent will say to the patient when a patient's plan of care is automatically sent to them."
            templateVariables={["patientFirstName", "patientLastName", "link", "clinicPhoneNumber"]}
            example="Hey {{ patientFirstName }}, your therapist would like you to schedule more appointments. To schedule visit: {{ link }}. You've been automatically added to the waitlist for any visits that you do not book in your plan. To update your waitlist availability, just ask."
            valueOverrides={templateValueOverrides}
          />
          {showSpanishTemplates && (
            <MessageTemplateRow
              label="Automated plan of care link sent text (Spanish)"
              value={planOfCareAutomatedTextEs}
              onChange={(value) => setPlanOfCareAutomatedTextEs(value)}
              description="Spanish version of the automated plan of care link sent text, used when the patient's language is Spanish."
              templateVariables={["patientFirstName", "patientLastName", "link", "clinicPhoneNumber"]}
              example="Hola {{ patientFirstName }}, su terapeuta desea que programe más citas. Para programar la visita: {{ link }}. Ha sido agregado automáticamente a la lista de espera para cualquier visita que no reserve en su plan. Para actualizar su disponibilidad en la lista de espera, solo pregunte."
              valueOverrides={templateValueOverrides}
            />
          )}
          <MessageTemplateRow
            label="Discharged text"
            value={dischargedText}
            onChange={(value) => setDischargedText(value)}
            description="This is the text the agent will say to a patient who is offered a waitlist spot but is already discharged/archived in Penciled."
            templateVariables={["patientFirstName", "patientLastName", "clinicPhoneNumber"]}
            example="I'm sorry, my records indicate that you are no longer an active patient at this clinic. Please contact the clinic at {{ clinicPhoneNumber }} for help."
            valueOverrides={templateValueOverrides}
          />
          {showSpanishTemplates && (
            <MessageTemplateRow
              label="Discharged text (Spanish)"
              value={dischargedTextEs}
              onChange={(value) => setDischargedTextEs(value)}
              description="Spanish version of the discharged text, used when the patient's language is Spanish."
              templateVariables={["patientFirstName", "patientLastName", "clinicPhoneNumber"]}
              example="Lo siento, mis registros indican que ya no es un paciente activo en esta clínica. Comuníquese con la clínica al {{ clinicPhoneNumber }} para obtener ayuda."
              valueOverrides={templateValueOverrides}
            />
          )}
          <MessageTemplateRow
            label="Auto-discharged text"
            value={autoDischargedText}
            onChange={(value) => setAutoDischargedText(value)}
            description="This is the text sent to the patient when their case is automatically discharged."
            templateVariables={["patientFirstName", "patientLastName", "organizationName", "clinicPhoneNumber"]}
            example="Hi {{ patientFirstName }}, this is {{ organizationName }}. Your case has been discharged. If you have any questions, feel free to reach out to us at {{ clinicPhoneNumber }}. Thank you for choosing {{ organizationName }}!"
            valueOverrides={templateValueOverrides}
          />
          {showSpanishTemplates && (
            <MessageTemplateRow
              label="Auto-discharged text (Spanish)"
              value={autoDischargedTextEs}
              onChange={(value) => setAutoDischargedTextEs(value)}
              description="Spanish version of the auto-discharged text, used when the patient's language is Spanish."
              templateVariables={["patientFirstName", "patientLastName", "organizationName", "clinicPhoneNumber"]}
              example="Hola {{ patientFirstName }}, somos {{ organizationName }}. Su caso ha sido dado de alta. Si tiene alguna pregunta, no dude en comunicarse con nosotros al {{ clinicPhoneNumber }}. ¡Gracias por elegir {{ organizationName }}!"
              valueOverrides={templateValueOverrides}
            />
          )}
          <MessageTemplateRow
            label="Appointment reminder text"
            value={reminderText}
            onChange={(value) => setReminderText(value)}
            description="This is the text the agent will say to the patient when they receive a reminder for an upcoming appointment."
            templateVariables={[
              "patientFirstName",
              "patientLastName",
              "patientPreferredName",
              "organizationName",
              "providerName",
              "appointmentDate",
              "appointmentTime",
              "appointmentTypeName",
              "clinicPhoneNumber",
              "daysBeforeReminder",
            ]}
            example="Hi {{ patientPreferredName }}, this is a reminder that you have a {{ appointmentTypeName }} appointment in {{ daysBeforeReminder }} days, {{ appointmentDate }} at {{ appointmentTime }} with {{ providerName }}. Are you going to be able to make it?"
            valueOverrides={{ ...templateValueOverrides, appointmentDate: "May 20" }}
          />
          {showSpanishTemplates && (
            <MessageTemplateRow
              label="Appointment reminder text (Spanish)"
              value={reminderTextEs}
              onChange={(value) => setReminderTextEs(value)}
              description="Spanish version of the appointment reminder text, used when the patient's language is Spanish."
              templateVariables={[
                "patientFirstName",
                "patientLastName",
                "patientPreferredName",
                "organizationName",
                "providerName",
                "appointmentDate",
                "appointmentTime",
                "appointmentTypeName",
                "clinicPhoneNumber",
                "daysBeforeReminder",
              ]}
              example="Hola {{ patientPreferredName }}, este es un recordatorio de que tiene una cita de {{ appointmentTypeName }} en {{ daysBeforeReminder }} días, el {{ appointmentDate }} a las {{ appointmentTime }} con {{ providerName }}. ¿Podrá asistir?"
              valueOverrides={{ ...templateValueOverrides, appointmentDate: "May 20" }}
            />
          )}
          <MessageTemplateRow
            label="Appointment cancelled text"
            value={appointmentCancelledText}
            onChange={(value) => setAppointmentCancelledText(value)}
            description="This is the text the agent will say to the patient when they cancel an appointment."
            templateVariables={["patientFirstName", "patientLastName", "organizationName", "appointmentDate", "clinicPhoneNumber", "link"]}
            example="Your appointment on {{ appointmentDate }} has been cancelled. You can reschedule here: {{ link }}. If you have any questions, please contact us at {{ clinicPhoneNumber }}."
            valueOverrides={templateValueOverrides}
          />
          {showSpanishTemplates && (
            <MessageTemplateRow
              label="Appointment cancelled text (Spanish)"
              value={appointmentCancelledTextEs}
              onChange={(value) => setAppointmentCancelledTextEs(value)}
              description="Spanish version of the appointment cancelled text, used when the patient's language is Spanish."
              templateVariables={["patientFirstName", "patientLastName", "organizationName", "appointmentDate", "clinicPhoneNumber", "link"]}
              example="Su cita el {{ appointmentDate }} ha sido cancelada. Puede reprogramar aquí: {{ link }}. Si tiene alguna pregunta, comuníquese con nosotros al {{ clinicPhoneNumber }}."
              valueOverrides={templateValueOverrides}
            />
          )}
        </Box>
        <Box mt={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <PrimaryButton onClick={handleSave} disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
          </PrimaryButton>
        </Box>
        <Typography variant="h6" sx={{ mt: 6, mb: 1 }}>
          Broadcast Text Message
        </Typography>
        <Typography variant="body2" sx={{ color: Colors.grey2, mb: 2 }}>
          Send a one-off text message to every patient. This does not use the agent and cannot be undone once sent.
        </Typography>
        <TextField
          value={broadcastMessage}
          onChange={(e) => setBroadcastMessage(e.target.value)}
          multiline
          rows={3}
          placeholder="Type the message you want to send to all patients..."
          sx={{
            "& .MuiInputBase-root": {
              background: Colors.textfield,
              borderRadius: "8px",
              color: Colors.white,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: Colors.grey1,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: Colors.primary,
            },
          }}
        />
        <Box mt={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <PrimaryButton
            onClick={() => setIsBroadcastConfirmOpen(true)}
            disabled={isBroadcasting || !broadcastMessage.trim()}
            sx={{
              background: Colors.error,
              "&:hover": {
                background: Colors.errorDark,
              },
            }}
          >
            {isBroadcasting ? <CircularProgress size={24} color="inherit" /> : "Send Broadcast"}
          </PrimaryButton>
        </Box>
        <Modal open={isBroadcastConfirmOpen} onClose={() => !isBroadcasting && setIsBroadcastConfirmOpen(false)}>
          <Box
            sx={{
              color: "#FFFFFF",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 480,
              bgcolor: Colors.bg2,
              border: "2px solid #000",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography variant="h6" mb={2}>
              Confirm Broadcast
            </Typography>
            <Typography variant="body1" mb={2}>
              This will immediately text <strong>every patient</strong> in this organization. This action cannot be undone.
            </Typography>
            <Box
              sx={{
                padding: "8px 15px",
                background: Colors.textfield,
                borderRadius: "8px",
                whiteSpace: "pre-wrap",
                mb: 3,
              }}
            >
              {broadcastMessage}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <TransparentButton onClick={() => setIsBroadcastConfirmOpen(false)} disabled={isBroadcasting}>
                Cancel
              </TransparentButton>
              <PrimaryButton
                onClick={handleBroadcast}
                disabled={isBroadcasting}
                sx={{
                  background: Colors.error,
                  "&:hover": {
                    background: Colors.errorDark,
                  },
                }}
              >
                {isBroadcasting ? <CircularProgress size={24} color="inherit" /> : "Send to All Patients"}
              </PrimaryButton>
            </Box>
          </Box>
        </Modal>
      </Box>
    </SettingsLayout>
  );
}
